/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Checkbox,
  checkboxClasses,
  FormControlLabel,
  FormGroup,
} from '@mui/material';
import React, { FC } from 'react';
import styled from 'styled-components';

import sign331170 from '../../assets/icons/331170.jpg';
import sign301201 from '../../assets/icons/301201.png';
import liquidoInflamavel from '../../assets/icons/liquidoinflamavel3.jpg';

import dayjs from 'dayjs';
import { ChecklistReportImages } from './ChecklistReportImages';
import { getProductNameByInternalCode } from '../../utils/getProductConfig';

interface EtanolReportProps {
  reportData: any;
}

type LabelProps = {
  $bordered?: boolean;
  $fullsize?: boolean;
};

type RowProps = {
  $noBottomBorder?: boolean;
};

type SectionProps = {
  $topBorder?: boolean;
};

const SectionHeader: FC<{ title: string; data?: string }> = ({
  title,
  data,
}) => {
  return (
    <SectionHeaderWrapper>
      <span
        className="sectionHeader"
        style={{
          background: 'lightgrey',
          minWidth: '60%',
          width: data ? '70.2%' : '100%',
          fontSize: '18px',
          fontWeight: 700,
          paddingLeft: '1.5rem',
          borderBottom: '1px solid grey',
          height: '40px',
          alignContent: 'center',
        }}
      >
        {title}
      </span>
      {data && (
        <span
          style={{
            display: 'flex',
            width: '35%',
            justifyContent: 'flex-start',
            alignItems: 'center',
            textAlign: 'left',
            paddingLeft: '1.5rem',
            gap: '0.5rem',
            borderBottom: '1px solid grey',
            borderLeft: '1px solid grey',
            margin: 0,
          }}
        >
          <span>
            <strong>Data:</strong>
          </span>
          <span>{data}</span>
        </span>
      )}
    </SectionHeaderWrapper>
  );
};

const InputText: FC<{
  label: string;
  value?: any;
  children?: any;

  noRightBorder?: boolean;
  displayDirection?: 'row' | 'column';
}> = ({
  label,
  value,
  children,
  noRightBorder,
  displayDirection = 'column',
}) => (
  <InputTextWrapper
    style={{
      borderRight: noRightBorder ? 'none' : '1px solid grey',
      flexDirection: displayDirection,
    }}
  >
    <Label>{label}</Label>

    <Value style={{ minHeight: '1.5rem' }}>{value}</Value>
    {children && (
      <div style={{ display: 'flex', paddingLeft: '1.5rem', gap: '1rem' }}>
        {children}
      </div>
    )}
  </InputTextWrapper>
);

const LabeledCheckbox: FC<{ label: string; checked: boolean }> = ({
  label,
  checked,
}) => (
  <FormControlLabel
    control={
      <Checkbox
        size="medium"
        checked={checked}
        sx={{
          display: 'flex',
          alignContent: 'center',
          [`&, &.${checkboxClasses.checked}`]: {
            color: '#00a859',
          },
          [`&, &.${checkboxClasses.colorSecondary}`]: {
            color: '#555552',
          },
        }}
      />
    }
    label={label}
  />
);

const BooleanField: FC<{
  label: string;
  trueValue: boolean;
  falseValue: boolean;
}> = ({ label, trueValue, falseValue }) => (
  <div
    style={{
      display: 'flex',
      width: '100%',
      paddingLeft: '1.5rem',
      alignItems: 'center',
      justifyContent: 'space-between',
    }}
  >
    <Label $fullsize style={{ width: '70%', borderRight: '1px solid grey' }}>
      {label}
    </Label>
    <FormGroup
      sx={{
        display: 'flex',
        flexDirection: 'row',
        width: '30%',
        justifyContent: 'center',
      }}
    >
      <LabeledCheckbox label="Sim" checked={trueValue} />
      <LabeledCheckbox label="Não" checked={falseValue} />
    </FormGroup>
  </div>
);

export const EtanolChecklistReport: FC<EtanolReportProps> = ({
  reportData,
}: EtanolReportProps) => {
  const formattedTodaysDate = () => {
    const formattedTodayDate = dayjs().format('DD/MM/YYYY');

    return formattedTodayDate;
  };

  return (
    <Container id="report-content">
      <DataWrapper>
        <div className="page">
          <Title>Checklist de Veículo - Transporte de Etanol</Title>

          <div
            style={{
              height: '0',
              borderBottom: '2px solid black',
              paddingTop: '1rem',
            }}
          ></div>

          {/* Identificacao */}
          <Section className="section-top-border">
            <SectionHeader
              title="1 - IDENTIFICAÇÃO"
              data={formattedTodaysDate()}
            />
            <Row>
              <InputText label="Nome do Motorista:" value={reportData.nome} />

              <InputText label="Celular:" value={reportData.celular} />

              <InputText
                label="CPF:"
                value={reportData.cpfmotorista}
                noRightBorder
              />
            </Row>

            <Row>
              <InputText label="Transportadora:" value={reportData.cnpj} />
              <InputText
                label="Cliente/Destino:"
                value={reportData.clienteDestino}
              />
              <InputText
                label="Tipo Produto:"
                value={getProductNameByInternalCode(reportData.produto)}
                noRightBorder
              />
            </Row>

            <Row>
              <InputText
                label="Validade Autorização Ambiental para TPP:"
                value={reportData.validadeTpp}
              />
              <InputText label="Placa Cavalo:" value={reportData.placacavalo} />
              <InputText
                label="Placa(s) Tanque(s):"
                value={
                  reportData.placa2 !== ''
                    ? `${reportData.placa} - ${reportData.placa2}`
                    : reportData.placa
                }
                noRightBorder
              />
            </Row>
          </Section>
          {/* Documentacao */}
          <Section $topBorder>
            <SectionHeader title="2- DOCUMENTAÇÃO" />
            <Row>
              <InputText label="Nº Registro da CNH:" value={reportData.cnh} />
              <InputText label="Validate CNH:" value={reportData.validadeCnh} />
              <InputText
                label="Categoria  CNH:"
                value={reportData.categoriaCnh}
                noRightBorder
              />
            </Row>

            <Row>
              <InputText label="Cert. Verif. de Veículo Tanque Rodoviário – Inmetro.">
                <div style={{}}>
                  <Label>Validade:</Label>
                  <Value>{reportData.cvvtr}</Value>
                </div>
              </InputText>
              <InputText
                label="Certificado de Inspeção Veicular (CIV) – Inmetro:"
                noRightBorder
              >
                <>
                  <div>
                    <Label>Val. Cavalo:</Label>
                    <Value>{reportData.civCavalo}</Value>
                  </div>

                  <div>
                    <Label>Val. Tanque:</Label>
                    <Value>{reportData.civTanque}</Value>
                  </div>
                </>
              </InputText>
            </Row>

            <Row>
              <InputText
                label="Validade MOPP:"
                value={reportData.validadeMopp}
                noRightBorder
              />
            </Row>

            <Row>
              <BooleanField
                label="O veículo possui AET – Autorização Especial de Trânsito:"
                trueValue={reportData.aet}
                falseValue={!reportData.aet}
              />
            </Row>
          </Section>

          {/* Motorista */}
          <Section className="section">
            <SectionHeader title="3 - MOTORISTA" />

            <Row>
              <BooleanField
                label="Trajado adequadamente (calça comprida, camisa e botina de segurança)?"
                trueValue={reportData.trajes}
                falseValue={!reportData.trajes}
              />
            </Row>
            <Row>
              <BooleanField
                label="Sem sinais de cansaço, sono ou embriagues?"
                trueValue={reportData.semSinaisCansaco}
                falseValue={!reportData.semSinaisCansaco}
              />
            </Row>
          </Section>

          {/* Veiculo */}
          <Section className="section" $topBorder>
            <SectionHeader title="4 - VEÍCULO" />
            <Row>
              <BooleanField
                label="Instalação elétrica (setas, lanternas, luz de freio, faróis em funcionamento e com fios isolados)?"
                trueValue={reportData.instalacoesEletricas}
                falseValue={!reportData.instalacoesEletricas}
              />
            </Row>

            <Row>
              <BooleanField
                label="Limpador de para-brisas funcionando?"
                trueValue={reportData.limpadores}
                falseValue={!reportData.limpadores}
              />
            </Row>

            <Row>
              <BooleanField
                label="Tanque em bom estado (sem sinais de corrosão, amassado ou com vazamentos)?"
                trueValue={reportData.tanquesOk}
                falseValue={!reportData.tanquesOk}
              />
            </Row>

            <Row>
              <BooleanField
                label="Tacógrafo funcionando?"
                trueValue={reportData.tacografo}
                falseValue={!reportData.tacografo}
              />
            </Row>

            <Row>
              <BooleanField
                label="Possui ponto para cabo de aterramento?"
                trueValue={reportData.pontoCaboAterramento}
                falseValue={!reportData.pontoCaboAterramento}
              />
            </Row>

            <Row>
              <BooleanField
                label="Pneus em bom estado (inclusive estepe)?"
                trueValue={reportData.pneusOk}
                falseValue={!reportData.pneusOk}
              />
            </Row>

            <Row>
              <BooleanField
                label="Faixas refletivas nas laterais, na traseira da carroceria e nas extremidades do para-choque?"
                trueValue={reportData.faixasRefletivas}
                falseValue={!reportData.faixasRefletivas}
              />
            </Row>
          </Section>
        </div>

        <div className="page">
          {/* Equipamentos de seguranca */}
          <Section className="section" $topBorder>
            <SectionHeader title="5 - EQUIPAMENTOS DE SEGURANÇA" />

            <Row>
              <BooleanField
                label="Possui extintor de incêndio (mínimo 01 de pó químico de 8 kg ou 02 de CO₂ de 6kg)?"
                trueValue={reportData.extintores}
                falseValue={!reportData.extintores}
              />
            </Row>

            <Row>
              <BooleanField
                label="Possui calços para rodas?"
                trueValue={reportData.calcosRodas}
                falseValue={!reportData.calcosRodas}
              />
            </Row>

            <Row>
              <BooleanField
                label="Possui cordas, fitas ou correntes de material plástico?"
                trueValue={reportData.cordasFitasCorrentes}
                falseValue={!reportData.cordasFitasCorrentes}
              />
            </Row>

            <Row>
              <BooleanField
                label="Possui placas de sinalização?"
                trueValue={reportData.placasSinalizacao}
                falseValue={!reportData.placasSinalizacao}
              />
            </Row>

            <Row>
              <BooleanField
                label="Possui cavaletes/cones de sinalização?"
                trueValue={reportData.cavaletesCones}
                falseValue={!reportData.cavaletesCones}
              />
            </Row>

            <Row>
              <BooleanField
                label="Possui lanterna a prova de explosão?"
                trueValue={reportData.lanternas}
                falseValue={!reportData.lanternas}
              />
            </Row>

            <Row>
              <BooleanField
                label="Possui óculos de segurança, protetor fácil ou máscara?"
                trueValue={reportData.oculosSeguranca}
                falseValue={!reportData.oculosSeguranca}
              />
            </Row>

            <Row>
              <BooleanField
                label="Possui capacete de segurança?"
                trueValue={reportData.capaceteSeguranca}
                falseValue={!reportData.capaceteSeguranca}
              />
            </Row>

            <Row>
              <BooleanField
                label="Possui luva de PVC forrada?"
                trueValue={reportData.luvasPvc}
                falseValue={!reportData.luvasPvc}
              />
            </Row>
          </Section>

          {/* Sinalizacao */}
          <Section $topBorder>
            <SectionHeader title="6 - SINALIZAÇÃO" />

            <Row>
              <div
                style={{
                  paddingLeft: '1.5rem',
                  width: '71%',
                  borderRight: '1px solid grey',
                }}
              >
                <Label>
                  {reportData.produto === 8
                    ? 'Possui painel de segurança 40x30 cm na cor laranja com números 30 – 1201 em preto?'
                    : 'Possui painel de segurança 40x30 cm na cor laranja com números 33 – 1170 em preto?'}
                </Label>

                <div style={{ width: '100%' }}>
                  <FormGroup
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      width: '100%',
                      paddingLeft: '1.5rem',
                      justifyContent: 'flex-start',
                    }}
                  >
                    <LabeledCheckbox
                      label="Frente"
                      checked={reportData.panelSegurancaFrente}
                    />
                    <LabeledCheckbox
                      label="Lateral Direita"
                      checked={reportData.panelSegurancaDireita}
                    />
                    <LabeledCheckbox
                      label="Lateral Esquerda"
                      checked={reportData.panelSegurancaEsquerda}
                    />
                    <LabeledCheckbox
                      label="Traseira"
                      checked={reportData.panelSegurancaTraseira}
                    />
                  </FormGroup>
                </div>
              </div>
              <div
                style={{
                  display: 'flex',
                  width: '30%',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <img
                  src={reportData.produto === 8 ? sign301201 : sign331170}
                  alt={reportData.produto === 8 ? 'Sign301201' : 'Sign331170'}
                  style={{ width: '60px', height: '60px' }}
                />
              </div>
            </Row>

            <Row>
              <div
                style={{
                  paddingLeft: '1.5rem',
                  width: '71%',
                  borderRight: '1px solid grey',
                }}
              >
                <Label>
                  Possui rótulo de risco 30x30 cm na cor vermelha com símbolo de
                  fogo e legenda “líquido inflamável” e número 3 em preto?
                </Label>

                <div style={{ width: '100%' }}>
                  <FormGroup
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      width: '100%',
                      paddingLeft: '1.5rem',
                      justifyContent: 'flex-start',
                    }}
                  >
                    <LabeledCheckbox
                      label="Frente"
                      checked={reportData.rotuloRiscoFrente}
                    />
                    <LabeledCheckbox
                      label="Lateral Direita"
                      checked={reportData.rotuloRiscoDireita}
                    />
                    <LabeledCheckbox
                      label="Lateral Esquerda"
                      checked={reportData.rotuloRiscoEsquerda}
                    />
                    <LabeledCheckbox
                      label="Traseira"
                      checked={reportData.rotuloRiscoTraseira}
                    />
                  </FormGroup>
                </div>
              </div>
              <div
                style={{
                  display: 'flex',
                  width: '30%',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <img
                  src={liquidoInflamavel}
                  alt="liquidoInflamavel"
                  style={{ width: '72px', height: '60px' }}
                />
              </div>
            </Row>

            <Row>
              <InputText
                label="Lacres:"
                value={reportData.lacres}
                displayDirection="row"
              />

              <InputText
                label="Observações:"
                value={reportData.observacoes}
                displayDirection="row"
              />
            </Row>

            <Row $noBottomBorder>
              <InputText
                label="Responsável pela Inspeção:"
                value={reportData.responsavel}
                displayDirection="row"
              />

              <InputText
                label="Horário da Inspeção:"
                value={reportData.horarioInspecao}
                noRightBorder
                displayDirection="row"
              />
            </Row>
          </Section>

          {/* Declaracoes do motorista */}
          <Section $topBorder>
            <SectionHeader title="7 - DECLARAÇÕES DO MOTORISTA" />

            <TextSection>
              <Row $noBottomBorder>
                <span style={{ paddingTop: '1rem' }}>
                  Declaro estar ciente da obrigação ao cumprimento dos itens
                  abaixo:
                </span>
              </Row>
              <Row $noBottomBorder>
                <span>
                  <strong>SEGURANÇA:</strong> É necessário a utilização do cinto
                  de segurança, conectado a linha de vida, caso tenha que subir
                  na carroceria e não tenha proteção coletiva contra queda.
                </span>
              </Row>

              <Row $noBottomBorder>
                <span>
                  <strong>SEGURANÇA DO ALIMENTO:</strong> É proibido o manuseio,
                  descarte ou consumo de alimento a partir de sua entrada na
                  portaria industrial.
                </span>
              </Row>

              <Row $noBottomBorder>
                {' '}
                <p>
                  <strong>REGISTRO E DIVULGAÇÃO DE IMAGEM:</strong> É proibido
                  fotografar, filmar e/ou divulgar colaboradores, eventos,
                  atividades laborais, processo operacionais, produtos e
                  estruturas da Bom Sucesso Agroindústria.
                </p>
              </Row>
              <Row $noBottomBorder>
                {' '}
                <p>
                  Atesto para os devidos fins, que todos os itens deste
                  checklist foram apresentados por mim durante a vistoria do
                  veículo nas dependências da Bom Sucesso Agroindústria S.A., e
                  que os mesmos acompanharão até o destino final.
                </p>
              </Row>
            </TextSection>
            <Row style={{ marginTop: '2.5rem' }}>
              <div
                style={{
                  display: 'flex',
                  width: '30%',
                  borderTop: '2px solid black',
                  justifyContent: 'center',
                  alignItems: 'center',
                  textAlign: 'center',
                  margin: '0 auto',
                  marginBottom: '2.5rem',
                }}
              >
                <span
                  style={{
                    fontWeight: 600,
                    fontSize: '18px',
                    marginTop: '1rem',
                  }}
                >
                  Assinatura do Motorista
                </span>
              </div>
            </Row>
          </Section>

          <Footer>
            <span style={{ fontWeight: 700, fontSize: '18px' }}>
              Bom Sucesso Agroindústria S.A
            </span>
            <span style={{}}>
              Rodovia GO-210, KM 335,1 – Zona Rural, Goiatuba, GO – CEP
              75600-000 – Telefone: (64) 3051-5090
            </span>
          </Footer>
        </div>

        <div className="page-three">
          {/* Imagens Checklist */}
          <Section $topBorder>
            <SectionHeader title="Imagens" />

            {!reportData.images && (
              <Row>
                <span
                  style={{ padding: '0.5rem 0 0.5rem 1.5rem', border: 'none' }}
                >
                  Nenhuma imagem adicionada.
                </span>
              </Row>
            )}

            <ChecklistReportImages
              images={reportData.images}
              imagesPerRow={2}
              startOnNewPage
            />
          </Section>
        </div>
      </DataWrapper>
    </Container>
  );
};

const Container = styled.div`
  display: none;

  @media print {
    display: block;
    visibility: visible;

    position: absolute;
    top: 0;
    left: 0;

    width: 100%;
    margin: 0 auto;
    padding-top: 10mm;

    background: white;

    .MuiTouchRipple-root {
      display: none !important;
    }

    .page {
      break-after: page;
    }

    .page:last-of-type {
      break-after: auto;
    }

    .section-top-border {
      border-top: 1px solid grey;
      margin-top: 1rem;
    }

    .section {
      border-left: 1px solid grey;
    }
  }
`;

const Title = styled.h1`
  font-size: 28px;
  padding-left: 1.5rem;

  margin: 0;
`;

const DataWrapper = styled.div``;

const Section = styled.div<SectionProps>`
  display: block;

  border-left: 1px solid grey;
  border-right: 1px solid grey;

  ${({ $topBorder }) =>
    $topBorder &&
    `
      border-top: 1px solid grey;
    `}
`;

const SectionHeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const InputTextWrapper = styled.div`
  display: flex;
  min-width: 300px;
  width: 100%;
  padding: 0.25rem 0 0.25rem 1rem;
`;

const Label = styled.span<LabelProps>`
  font-size: 1rem;
  font-weight: 600;
  height: ${({ $fullsize }) => ($fullsize ? '100%' : 'auto')};
  align-content: ${({ $fullsize }) => ($fullsize ? 'center' : 'flex-start')};
`;

const Value = styled.span`
  padding-left: 1.5rem;
  min-height: '57px';
`;

const Row = styled.div<RowProps>`
  display: flex;

  justify-content: space-between;
  border-bottom: ${({ $noBottomBorder }) =>
    $noBottomBorder ? 'none' : '1px solid grey'};
`;

const TextSection = styled.div`
  padding-left: 1.5rem;
  border: none;
`;

const Footer = styled.div`
  display: flex;
  flex-direction: column;

  width: 100%;

  justify-content: center;
  align-items: center;
`;
